import ResendTokenModal from '@/components/ResendTokenModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { APP_VERSION } from '@/config/version';
import { useToast } from '@/contexts/ToastContext';
import { selectPhoneAndSendOtp, verifyCode } from '@/services';
import { getDocumentStatus } from '@/services/Document/document-service';
import { OtpType } from '@/types';
import { ArrowLeft } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface LocationState {
  phoneId: string;
  phoneNumber: string;
  otpType: OtpType;
  code?: string;
}

const VerificationCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const { showToast } = useToast();

  const [code, setCode] = useState<string[]>(new Array(6).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentOtpType, setCurrentOtpType] = useState<OtpType>(state?.otpType || OtpType.PHONE);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const phoneNumber = state?.phoneNumber || '';

  // Timer para reenvio
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (index: number, value: string) => {
    // Permitir apenas números
    if (!/^\d*$/.test(value)) return;

    // Limpar erro quando usuário começar a digitar
    if (hasError) {
      setHasError(false);
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Mover para o próximo campo se um dígito foi inserido
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Voltar para o campo anterior se Backspace foi pressionado e o campo está vazio
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);
    const newCode = new Array(6).fill('');

    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }

    setCode(newCode);

    // Limpar erro quando usuário colar código
    if (hasError) {
      setHasError(false);
    }

    // Focar no último campo preenchido ou no próximo vazio
    const nextEmptyIndex = newCode.findIndex(digit => !digit);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const maskPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length >= 10) {
      const ddd = cleaned.slice(0, 2);
      const lastPart = cleaned.slice(7, 11);
      return `(${ddd})*****-${lastPart}`;
    }
    return phone;
  };

  const handleSubmit = async () => {
    const enteredCode = code.join('');

    if (enteredCode.length !== 6) {
      showToast({
        title: 'Por favor, insira o código completo de 6 dígitos.',
        type: 'error',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Verificar código via API
      await verifyCode({
        code: enteredCode,
        type: currentOtpType,
      });

      // Se chegou até aqui, significa que retornou status 200 (código correto)
      showToast({
        title: 'Código verificado com sucesso!',
        type: 'success',
      });

      // Verificar status do usuário
      const userStatus = getDocumentStatus();

      if (userStatus === 'active') {
        // Se o usuário já está ativo, ir direto para tela de sucesso
        navigate('/success');
      } else {
        // Se o usuário não está ativo, ir para verificação de email
        navigate('/email-verification');
      }
    } catch (error: any) {
      console.error('Erro ao verificar código:', error);
      // Mostrar erro visual em vez de toast
      setHasError(true);
      setCode(new Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    if (!canResend) return;
    setShowResendModal(true);
  };

  const handleResendWhatsApp = async () => {
    setShowResendModal(false);
    setIsLoading(true);
    setCanResend(false);
    setResendTimer(60);

    try {
      // Reenviar código usando a mesma função de envio de WhatsApp para o número escolhido
      await selectPhoneAndSendOtp({
        phoneId: state.phoneId,
        type: OtpType.PHONE,
      });

      // Atualizar o tipo OTP atual
      setCurrentOtpType(OtpType.PHONE);

      // Se chegou até aqui, significa que retornou status 200 (sucesso)
      showToast({
        title: 'Código reenviado via WhatsApp!',
        type: 'success',
      });
    } catch (error: any) {
      console.error('Erro ao reenviar código via WhatsApp:', error);
      showToast({
        title: 'Erro ao reenviar código via WhatsApp. Tente novamente.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Função para reenviar o código de verificação via SMS
   *
   * Esta função é chamada quando o usuário solicita o reenvio do código OTP
   * através do canal SMS. Ela fecha o modal de reenvio, atualiza os estados
   * de loading e timer, e faz a chamada para a API de envio de OTP.
   *
   * @async
   * @function handleResendSMS
   * @returns {Promise<void>} Promise que resolve quando a operação é concluída
   */
  const handleResendSMS = async () => {
    setShowResendModal(false);
    setIsLoading(true);
    setCanResend(false);
    setResendTimer(60);

    try {
      // Reenviar código via SMS
      await selectPhoneAndSendOtp({
        phoneId: state.phoneId,
        type: OtpType.SMS,
      });

      // Atualizar o tipo OTP atual
      setCurrentOtpType(OtpType.SMS);

      // Se chegou até aqui, significa que retornou status 200 (sucesso)
      showToast({
        title: 'Código enviado via SMS!',
        type: 'success',
      });
    } catch (error: any) {
      console.error('Erro ao enviar código via SMS:', error);
      showToast({
        title: 'Erro ao enviar código via SMS. Tente novamente.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isCodeComplete = code.every(digit => digit !== '');

  return (
    <div className="min-h-screen bg-background-gradient-primary">
      {/* Main Content */}
      <div className="flex flex-col items-center justify-center min-h-screen px-5">
        <div className="w-full max-w-lg">
          <Card className="shadow-xl border-0 backdrop-blur-sm md:px-6 py-4 md:py-6 gap-0">
            <CardHeader className="pb-0">
              {/* Header with Back Button and Logo */}
              <div className="mb-6 flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <ArrowLeft className="h-6 w-6 text-gray-600" />
                </button>
                <img
                  src="/icon_oi_tv.svg"
                  alt="Oi TV"
                  className="h-12 w-auto object-contain"
                />
                <div className="w-10"></div>{' '}
                {/* Spacer para manter o logo centralizado */}
              </div>

              {/* Title */}
              <div className="space-y-2 text-left">
                <h1 className="text-2xl font-bold">Confirme seu Whatsapp</h1>
                <p className="text-sm text-gray-600">
                  Insira o código enviado para o número{' '}
                  {maskPhoneNumber(phoneNumber)}
                </p>
              </div>
            </CardHeader>

            <CardContent className="pt-6">
              <div className="space-y-6">
                {/* Code Input Fields */}
                <div className="flex justify-start gap-2 md:gap-3">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleInputChange(index, e.target.value)}
                      onKeyDown={e => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      className={`w-10 h-10 md:w-14 md:h-12 text-center text-base md:text-lg font-semibold border rounded-lg focus:outline-none focus:ring-2 ${
                        hasError
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-input focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                      }`}
                      disabled={isLoading}
                    />
                  ))}
                </div>

                {/* Error Message */}
                {hasError && (
                  <div className="flex items-center justify-center gap-2 text-red-500 text-sm">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-bold">Código inválido</span>
                  </div>
                )}

                {/* Resend Code */}
                <div className="text-center">
                  <p className="text-sm text-primary">
                    {canResend ? (
                      <button
                        onClick={handleResendCode}
                        className="text-primary hover:underline font-medium"
                        disabled={isLoading}
                      >
                        Não recebeu o código?
                      </button>
                    ) : (
                      <span className="text-gray-500">
                        Não recebeu o código? ({resendTimer}s)
                      </span>
                    )}
                  </p>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    onClick={handleSubmit}
                    className="w-full bg-primary hover:bg-green-600"
                    disabled={!isCodeComplete || isLoading}
                  >
                    {isLoading ? 'Verificando...' : 'Continuar'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-center items-center mt-4">
            <span className="text-xs text-white">{APP_VERSION}</span>
          </div>
        </div>
      </div>

      {/* Modal de reenvio */}
      <ResendTokenModal
        isOpen={showResendModal}
        onClose={() => setShowResendModal(false)}
        onResendWhatsApp={handleResendWhatsApp}
        onResendSMS={handleResendSMS}
      />
    </div>
  );
};

export default VerificationCode;
